import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Row, Col, Badge, Container, Modal, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft, faHistory, faInfoCircle, faExpandAlt, faCopyright, faArrowRight
} from "@fortawesome/free-solid-svg-icons";
import useFetch from "../../../../hooks/useFetch";

const API_URL = process.env.REACT_APP_API_URL;

export default function IconografiaDetalhe() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { get } = useFetch();

    const [showModal, setShowModal] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);

    const [state, setState] = useState({ item: null, loading: true });

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [pathname]);

    useEffect(() => {
        async function carregar() {
            try {
                let response = await get(`/acervo/fotografias/${id}`);
                if (response.ok && response.result.data) {
                    setState({ item: response.result.data, loading: false });
                } else {
                    setState({ item: null, loading: false });
                }
            } catch (error) {
                console.error("Erro no carregar():", error);
                setState({ item: null, loading: false });
            }
        }
        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (state.loading) {
        return (
            <Container className="py-5" style={{ marginTop: '100px' }}>
                <div className="text-center"><div className="spinner-border text-teal"></div></div>
            </Container>
        );
    }

    const item = state.item;

    if (!item) {
        return (
            <Container className="py-5" style={{marginTop: '100px'}}>
                <div className="text-center">
                    <h3 className="text-muted">Item não encontrado</h3>
                    <Button variant="teal" onClick={() => navigate('/acervo-iconografico')} className="mt-3">
                        Voltar para a busca
                    </Button>
                </div>
            </Container>
        );
    }

    const fotoSrc = `${API_URL}/acervo/fotografias/${item.id_fotografia}/imagem`;
    const pessoas = item.PessoaRetratadas ?? item.PessoaRetratada ?? [];
    const tags = (item.PalavraChaveFotografias ?? item.PalavraChaveFotografia ?? []).map((p) => p.palavra);
    const dimensoes = item.dimensoes_altura && item.dimensoes_largura
        ? `${item.dimensoes_altura} x ${item.dimensoes_largura} cm`
        : null;
    const localizacaoFisica = item.Localizacao
        ? [item.Localizacao.sala, item.Localizacao.estante, item.Localizacao.prateleira, item.Localizacao.caixa, item.Localizacao.envelope_pasta].filter(Boolean).join(", ")
        : null;

    // Funções de manipulação
    const handleMouseDown = (e) => {
        if (zoom > 1) {
            setIsDragging(true);
            setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        const nextX = e.clientX - startPos.x;
        const nextY = e.clientY - startPos.y;
        const containerWidth = e.currentTarget.offsetWidth;
        const containerHeight = e.currentTarget.offsetHeight;
        const maxX = Math.max(0, (containerWidth * zoom - containerWidth) / 2);
        const maxY = Math.max(0, (containerHeight * zoom - containerHeight) / 2);
        setPosition({
            x: Math.max(-maxX, Math.min(nextX, maxX)),
            y: Math.max(-maxY, Math.min(nextY, maxY))
        });
    };

    const stopDragging = () => setIsDragging(false);
    const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.5, 4));
    const handleZoomOut = () => {
        setZoom(prev => {
            const newZoom = Math.max(prev - 0.5, 1);
            if (newZoom === 1) setPosition({ x: 0, y: 0 });
            return newZoom;
        });
    };
    const resetZoom = () => { setZoom(1); setPosition({ x: 0, y: 0 }); };
    const fecharModal = () => { setShowModal(false); resetZoom(); };

    const getDataExibicao = () => {
        if (item.data_producao) return item.data_producao;
        if (item.ano_producao) return String(item.ano_producao);
        return "Não informada";
    };

    return (
        <div className="detail-page-wrapper pb-5">
            <Container>
                <div className="mb-4 d-flex justify-content-between align-items-center">
                    <button onClick={() => navigate(-1)} className="btn-back-modern">
                        <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
                        Voltar à página anterior
                    </button>
                    <Badge bg="dark" className="px-3 py-2 badge-tombo-detail">{item.numero_tombo}</Badge>
                </div>

                <div className="section-title-modern mb-5">
                    <div className="title-accent"></div>
                    <h2 className="title-text">Detalhes da Iconografia</h2>
                </div>

                <Row className="g-5">
                    <Col lg={7}>
                        <div className="photo-detail-container mb-4" onClick={() => setShowModal(true)}>
                            <div className="expand-overlay">
                                <FontAwesomeIcon icon={faExpandAlt} />
                                <span>Clique para ampliar</span>
                            </div>
                            <img src={fotoSrc} alt={item.titulo_atribuido} className="img-main-detail" onError={(e) => { e.target.style.display = 'none'; }} />
                            <div className="photo-caption">
                                Foto: {item.AutorFotografo?.nome ?? "Não identificado"} ({item.ano_producao ?? "-"})
                            </div>
                        </div>

                        {item.observacoes && (
                            <div className="mb-5 p-3 bg-white rounded shadow-sm border-start border-teal border-4">
                                <span className="tech-label d-block mb-1" style={{fontSize: '0.75rem', fontWeight: 800, color: '#94a3b8'}}>OBSERVAÇÃO</span>
                                <p className="small text-dark mb-0">{item.observacoes}</p>
                            </div>
                        )}

                        <div className="narrative-section-inline mb-5">
                            <h5 className="narrative-title mb-4">
                                <FontAwesomeIcon icon={faHistory} className="me-2 text-teal" />
                                Narrativa Histórica
                            </h5>
                            <div className="narrative-text-content mb-4" style={{textAlign: 'justify'}}>
                                {item.contexto_historico || "Nenhuma contextualização histórica cadastrada."}
                            </div>

                            {tags.length > 0 && (
                                <div className="tag-section-inline pt-4 border-top">
                                    <h6 className="small fw-bold text-muted mb-3">PALAVRAS-CHAVES</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {tags.map(tag => (
                                            <Badge key={tag} bg="light" text="dark" className="border fw-normal py-2 px-3">
                                                #{tag}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Col>

                    <Col lg={5}>
                        <div className="info-side-panel sticky-top" style={{ top: '120px' }}>
                            <h1 className="photo-main-title">{item.titulo_atribuido}</h1>
                            <p className="photo-summary-full text-muted mb-4">
                                {item.descricao}
                            </p>

                            <div className="technical-grid-container">
                                <h6 className="grid-label-title">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    ESPECIFICAÇÕES TÉCNICAS
                                </h6>
                                <div className="technical-grid">
                                    <TechnicalItem label="CÓD. REFERÊNCIA" value={item.codigo_referencia} />
                                    <TechnicalItem label="DATA / PERÍODO" value={getDataExibicao()} />
                                    <TechnicalItem label="LOCALIZAÇÃO FÍSICA" value={localizacaoFisica} />

                                    {item.AutorFotografo && (
                                        <TechnicalItem
                                            label="AUTOR"
                                            value={item.AutorFotografo.nome}
                                            link={`/acervo/autor/${item.id_autor}`}
                                        />
                                    )}

                                    {item.AcervoDetentor && (
                                        <TechnicalItem label="ACERVO" value={item.AcervoDetentor.nome} />
                                    )}

                                    <TechnicalItem label="LOCAL" value={item.local_producao} />

                                    {item.EventoDocumentado && (
                                        <TechnicalItem
                                            label="EVENTO"
                                            value={item.EventoDocumentado.nome_evento}
                                            link={`/acervo/evento/${item.id_evento}`}
                                        />
                                    )}

                                    <div className="tech-item mb-2">
                                        <span className="tech-label d-block" style={{fontSize: '0.65rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase'}}>PESSOAS RETRATADAS</span>
                                        <div className="d-flex flex-column">
                                            {pessoas.length > 0 ? (
                                                pessoas.map(p => (
                                                    <Link key={p.id_pessoa} to={`/acervo/pessoa/${p.id_pessoa}`} className="tech-link-highlight text-decoration-none d-inline-flex align-items-center">
                                                        {p.nome}
                                                        <FontAwesomeIcon icon={faArrowRight} className="ms-1 icon-sm-hover" />
                                                    </Link>
                                                ))
                                            ) : (
                                                <span className="tech-value" style={{fontSize: '0.9rem', color: '#ccc'}}>Nenhuma identificada</span>
                                            )}
                                        </div>
                                    </div>
                                    <TechnicalItem label="TÉCNICA" value={item.tecnica} />
                                    <TechnicalItem label="CROMATISMO" value={item.cor} />
                                    <TechnicalItem label="SUPORTE" value={item.suporte} />
                                    <TechnicalItem label="DIMENSÕES" value={dimensoes} />
                                    <TechnicalItem label="CONSERVAÇÃO" value={item.estado_conservacao} />
                                    <TechnicalItem label="CATEGORIA" value={item.CategoriaFotografia?.nome_categoria} />
                                    {item.SubcategoriaFotografia?.nome_subcategoria && (
                                        <TechnicalItem label="SUBCATEGORIA" value={item.SubcategoriaFotografia.nome_subcategoria} />
                                    )}
                                </div>

                                {item.DireitoAutoral && (
                                    <div className="mt-3 pt-3 border-top">
                                        <span className="tech-label d-block mb-1" style={{fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8'}}>CONDIÇÕES DE REPRODUÇÃO</span>
                                        <span className="tech-value rights-text">
                                            <FontAwesomeIcon icon={faCopyright} className="me-1 text-muted" /> {item.DireitoAutoral.nome_direito}{item.condicoes_reproducao ? ` — ${item.condicoes_reproducao}` : ""}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>

            <Modal show={showModal} onHide={fecharModal} size="xl" centered contentClassName="bg-transparent border-0 shadow-none" className="photo-lightbox">
                <div className="d-flex justify-content-between align-items-center p-3">
                    <div className="d-flex gap-2">
                        <Button variant="light" onClick={handleZoomOut} className="rounded-circle shadow-sm" style={{ width: '40px', height: '40px' }}>-</Button>
                        <Button variant="light" onClick={handleZoomIn} className="rounded-circle shadow-sm" style={{ width: '40px', height: '40px' }}>+</Button>
                        <Button variant="light" onClick={resetZoom} className="rounded-pill px-3 shadow-sm">Resetar</Button>
                    </div>
                    <button onClick={fecharModal} className="btn-close btn-close-white shadow-none"></button>
                </div>
                <Modal.Body className="p-0 text-center" style={{ overflow: 'hidden', maxHeight: '85vh', cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
                    onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={stopDragging} onMouseLeave={stopDragging}>
                    {/* Sem width:100% fixo no wrapper/imagem: numa foto retrato
                        (mais alta que larga), forçar width:100% deixava a altura
                        maior que o modal (max-height:85vh + overflow:hidden), e
                        ela ficava cortada em cima/embaixo. Deixando o <img> livre
                        pra usar maxWidth/maxHeight (mantendo proporção), ele
                        encaixa inteiro tanto retrato quanto paisagem. */}
                    <div style={{ transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`, transition: isDragging ? 'none' : 'transform 0.3s ease', display: 'inline-block' }}>
                        <img src={fotoSrc} alt="Preview" className="rounded shadow-lg" draggable="false" style={{ maxWidth: '90vw', maxHeight: '85vh', width: 'auto', height: 'auto', objectFit: 'contain' }} />
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
}

const TechnicalItem = ({ label, value, link }) => {
    if (!value) return null;
    return (
        <div className="tech-item mb-2">
            <span className="tech-label d-block" style={{fontSize: '0.65rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase'}}>{label}</span>
            {link ? (
                <Link to={link} className="tech-link-highlight text-decoration-none d-inline-flex align-items-center">
                    {value}
                    <FontAwesomeIcon icon={faArrowRight} className="ms-1 icon-sm-hover" />
                </Link>
            ) : (
                <span className="tech-value" style={{fontSize: '0.9rem', fontWeight: 600, color: '#2c3e50'}}>{value}</span>
            )}
        </div>
    );
};
